/**
 * JWT 过期判定（**只解 payload，不校验签名**）。
 *
 * 用途：启动时判断「本地这份 access token 还能用吗」——能用就不打 `/auth/refresh`。
 *
 * 为什么值得单拎出来：`/auth/refresh` 是**轮换型**刷新（成功后旧 refresh token 作废），
 * 启动流程与 401 拦截器若并发各刷一次，其中一次必然 401（2026-09-25 线上那条
 * `401 POST /api/v1/auth/refresh` 的来源）。少发一次不必要的刷新，就少一整类竞态。
 */
export interface JwtPayload {
  exp?: number;
  [key: string]: unknown;
}

/** 解出 payload；格式不合法时返回 null（不抛） */
export function decodeJwt(token: string): JwtPayload | null {
  const parts = token.split(".");
  if (parts.length < 2 || !parts[1]) return null;
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const binary = atob(padded);
    // atob 只给 latin1：含中文 claim（displayName 等）时必须按 UTF-8 还原
    const json = decodeURIComponent(
      Array.from(binary, (ch) => `%${`00${ch.charCodeAt(0).toString(16)}`.slice(-2)}`).join(""),
    );
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

/** 剩余有效秒数；解析不出或没有 exp 时返回 null（= 未知） */
export function tokenRemainingSeconds(token: string, now: number = Date.now()): number | null {
  const payload = decodeJwt(token);
  if (!payload || typeof payload.exp !== "number") return null;
  return payload.exp - Math.floor(now / 1000);
}

/**
 * token 是否仍「新鲜」（剩余寿命 > marginSeconds）。
 * 解析不出（或没有 exp）时一律返回 **false**：宁可多刷一次，也不要拿一份可疑 token 去撞 401。
 */
export function isTokenFresh(token: string, marginSeconds = 60, now: number = Date.now()): boolean {
  const remaining = tokenRemainingSeconds(token, now);
  return remaining !== null && remaining > marginSeconds;
}
